import React, { useMemo, useCallback } from 'react';
import { Select, Row, Col, Space, Typography } from 'antd';
import constants from 'core/constants/Constants';
import { useTranslate } from '@refinedev/core';
// prettier-ignore
import { buildJsonLogic, getQueryWithValues, getReadableFormula } from 'pages/rules/utils/payoutUtils';
// prettier-ignore
import { FlatFormula, PercentageFormula, FlatWithConstraints, PercentageWithConstraints } from 'pages/rules/utils/payoutFormulas';
import PayoutModals from 'pages/rules/utils/PayoutModals';
// prettier-ignore
import { FieldsState, PayoutConfig, SimplePayoutBuilderProps } from 'pages/rules/type';

const { Option } = Select;
const { Text } = Typography;

const SimplePayoutBuilder: React.FC<SimplePayoutBuilderProps> = ({
  configFields,
  value,
  onChange,
  disabled = false,
  showButtons = false,
  form,
  name = 'output_formula',
}) => {
  const [isQueryModalVisible, setIsQueryModalVisible] = React.useState(false);
  const [isPayoutTypeModalVisible, setIsPayoutTypeModalVisible] =
    React.useState(false);
  const [isJsonModalVisible, setIsJsonModalVisible] = React.useState(false);
  const t = useTranslate();
  console.log(configFields, 'configFieldsjjjj');

  // Filter numeric fields
  const fieldOptions = useMemo(
    () =>
      configFields
        .filter(
          (field) =>
            field?.dataType?.toLowerCase() === 'number' ||
            field?.dataType?.toLowerCase() === 'decimal'
        )
        .map((item) => ({
          value: item?.label,
          label: item?.label,
        })),
    [configFields]
  );

  // Parse current value or use defaults
  const currentConfig: PayoutConfig = useMemo(() => {
    if (value && typeof value === 'object') {
      return {
        type: value.type || 'percentage',
        fields: value.fields || {
          field1: null,
          field2: null,
          minValue: '',
          maxValue: '',
          minField: null,
          maxField: null,
        },
        jsonLogic: value.jsonLogic || null,
        query: value.query || {},
        value: value.value || '',
      };
    }
    return {
      type: 'percentage',
      fields: {
        field1: null,
        field2: null,
        minValue: '',
        maxValue: '',
        minField: null,
        maxField: null,
      },
      jsonLogic: null,
      query: {},
      value: '',
    };
  }, [value]);

  // Update parent form when config changes
  const updateConfig = useCallback(
    (type: string, fields: FieldsState) => {
      const jsonLogic = buildJsonLogic(type, fields);
      const queryValue = getQueryWithValues(type, fields);

      const newConfig: PayoutConfig = {
        type,
        fields,
        jsonLogic,
        query: {},
        value: queryValue,
      };

      // Update form field
      if (form && name) {
        form.setFieldsValue({ [name]: newConfig });
      }

      // Call onChange callback
      if (onChange) {
        onChange(newConfig);
      }
    },
    [form, name, onChange]
  );

  // Handle payout type change
  const handleTypeChange = useCallback(
    (newType: string) => {
      const resetFields: FieldsState = {
        field1: null,
        field2: null,
        minValue: '',
        maxValue: '',
        minField: null,
        maxField: null,
      };
      updateConfig(newType, resetFields);
    },
    [updateConfig]
  );

  // Handle field change
  const handleFieldChange = useCallback(
    (fieldName: keyof FieldsState, fieldValue: string) => {
      const updatedFields = {
        ...currentConfig.fields,
        [fieldName]: fieldValue,
      };
      updateConfig(currentConfig.type, updatedFields);
    },
    [currentConfig, updateConfig]
  );

  // Filter option for Select components
  const filterOption = (input: string, option: any) =>
    option?.children?.toString().toLowerCase().includes(input.toLowerCase());

  // Render formula builder based on type
  const renderFormulaBuilder = () => {
    if (!currentConfig.type) return null;

    const formulaProps = {
      currentConfig,
      fieldOptions,
      handleFieldChange,
      disabled,
      t,
      getReadableFormula,
      filterOption,
    };

    if (currentConfig.type === 'percentage') {
      return <PercentageFormula {...formulaProps} />;
    }
    if (currentConfig.type.startsWith('percentage_')) {
      return <PercentageWithConstraints {...formulaProps} />;
    }
    if (currentConfig.type === 'flat') {
      return <FlatFormula {...formulaProps} />;
    }
    if (currentConfig.type.startsWith('flat_')) {
      return <FlatWithConstraints {...formulaProps} />;
    }
    return null;
  };

  return (
    <div className="p-4 border border-gray-300 rounded-xl bg-purple-50">
      <Space direction="vertical" className="w-full" size="large">
        <Row gutter={8}>
          <Col span={12}>
            <Text strong className="block mb-2 text-base">
              {t('simplePayoutBuilder.selectPayoutType')}{' '}
              <span className="a8-error">*</span>
            </Text>
            <Select
              placeholder="Select payout calculation type"
              value={currentConfig.type}
              onChange={handleTypeChange}
              disabled={disabled}
              className="w-full"
              showSearch
              filterOption={filterOption}
            >
              {constants.payoutTypes?.map((type: any) => (
                <Option key={type.value} value={type.value}>
                  {type.label}
                </Option>
              ))}
            </Select>
          </Col>
          <Col className="mt-8">
            <Text
              className="text-blue-800 text-base cursor-pointer hover:underline"
              onClick={() => setIsPayoutTypeModalVisible(true)}
            >
              {t('simplePayoutBuilder.title')}
            </Text>
          </Col>
        </Row>

        {showButtons ? (
          <div className="flex flex-row gap-3">
            <Text
              className="text-blue-800 text-base cursor-pointer hover:underline"
              onClick={() => setIsQueryModalVisible(true)}
            >
              {t('simplePayoutBuilder.viewQuery')}
            </Text>
            <Text
              className="text-blue-800 text-base cursor-pointer hover:underline"
              onClick={() => setIsJsonModalVisible(true)}
            >
              {t('simplePayoutBuilder.viewJSONLogic')}
            </Text>
          </div>
        ) : null}

        <Row>
          <Col span={24}>{renderFormulaBuilder()}</Col>
        </Row>
      </Space>

      <PayoutModals
        visible={{
          query: isQueryModalVisible,
          json: isJsonModalVisible,
          type: isPayoutTypeModalVisible,
        }}
        onClose={(modalName) => {
          if (modalName === 'query') setIsQueryModalVisible(false);
          if (modalName === 'json') setIsJsonModalVisible(false);
          if (modalName === 'type') setIsPayoutTypeModalVisible(false);
        }}
        currentConfig={currentConfig}
        queryValue={getQueryWithValues(
          currentConfig.type,
          currentConfig.fields
        )}
        t={t}
      />
    </div>
  );
};

export default SimplePayoutBuilder;
